import { useState } from 'react';
import { X, Sparkles, Check, Loader, Database, FileText, AlertCircle } from 'lucide-react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';
import Card, { CardBody } from './ui/Card';

interface DataSuggestion {
  requirementEntity: string;
  requirementCategory: 'master_data' | 'transactional_data';
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  typicalLocation?: string;
  matchesExisting?: boolean;
}

interface AdditionalDataSuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  systemName: string;
  caseId: string;
  onApprove: (approvedSuggestions: DataSuggestion[]) => Promise<void>;
  isResearching: boolean;
  onResearch: (userHints: string) => Promise<DataSuggestion[]>;
}

export default function AdditionalDataSuggestionsModal({
  isOpen,
  onClose,
  systemName,
  caseId,
  onApprove,
  isResearching,
  onResearch
}: AdditionalDataSuggestionsModalProps) {
  const [userHints, setUserHints] = useState('');
  const [suggestions, setSuggestions] = useState<DataSuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [hasResearched, setHasResearched] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const handleResearch = async () => {
    if (!userHints.trim()) return;

    try {
      const results = await onResearch(userHints);
      setSuggestions(results);
      // Auto-select all suggestions
      setSelectedSuggestions(new Set(results.map(s => s.requirementEntity)));
      setHasResearched(true);
    } catch (error) {
      console.error('Research failed:', error);
    }
  };

  const toggleSelection = (entity: string) => {
    setSelectedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entity)) {
        newSet.delete(entity);
      } else {
        newSet.add(entity);
      }
      return newSet;
    });
  };

  const handleApprove = async () => {
    const approved = suggestions.filter(s => selectedSuggestions.has(s.requirementEntity));
    if (approved.length === 0) return;

    setIsApproving(true);
    try {
      await onApprove(approved);
      // Reset state and close
      setUserHints('');
      setSuggestions([]);
      setSelectedSuggestions(new Set());
      setHasResearched(false);
      onClose();
    } catch (error) {
      console.error('Approval failed:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleCancel = () => {
    setUserHints('');
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setHasResearched(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary-600" />
                Suggest Additional Data Sources
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                for <span className="font-semibold">{systemName}</span>
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              disabled={isResearching || isApproving}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {!hasResearched ? (
              // Input stage
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900 mb-1">
                        How this works
                      </h4>
                      <p className="text-sm text-blue-800 mb-2">
                        Business Transformation Studio might have missed some data sources available in {systemName}.
                        Provide hints about additional tables, entities, or data types you know exist.
                      </p>
                      <p className="text-xs text-blue-700 italic">
                        Example: "EKBZ, EKBE, Bank_guarantee tables", "Purchase requisition history", "Vendor payment terms"
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-900 mb-2">
                    Your Suggestions
                  </label>
                  <Textarea
                    value={userHints}
                    onChange={(e) => setUserHints(e.target.value)}
                    rows={8}
                    placeholder={`Enter table names, entity names, or keywords that might be available in ${systemName}...

Examples:
• EKBZ, EKBE (Purchase Order History tables)
• Bank_guarantee data
• Vendor payment terms and conditions
• Material reservation documents
• Quality inspection results`}
                    disabled={isResearching}
                  />
                  <p className="text-xs text-neutral-600 mt-2">
                    Be specific about table names, modules, or data entities you know exist in {systemName}
                  </p>
                </div>

                <Button
                  onClick={handleResearch}
                  disabled={!userHints.trim() || isResearching}
                  loading={isResearching}
                  className="w-full"
                >
                  <Sparkles className="w-4 h-4" />
                  {isResearching ? 'Business Transformation Studio is analyzing...' : 'Research Additional Data'}
                </Button>
              </div>
            ) : (
              // Results stage
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-green-900 mb-1">
                        Research Complete
                      </h4>
                      <p className="text-sm text-green-800">
                        Found {suggestions.length} additional data {suggestions.length === 1 ? 'source' : 'sources'} in {systemName}.
                        Review and approve the ones you want to add.
                      </p>
                    </div>
                  </div>
                </div>

                {suggestions.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500">
                    <p className="text-sm font-medium">No new data sources to add</p>
                    <p className="text-xs mt-2">
                      The data sources you mentioned are either already mapped to {systemName} or not recognized as valid data entities.
                    </p>
                    <p className="text-xs mt-1 italic">
                      Try different keywords, table names, or entity types.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-neutral-900">
                        Select data sources to add ({selectedSuggestions.size} selected)
                      </h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedSuggestions(new Set(suggestions.map(s => s.requirementEntity)))}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Select All
                        </button>
                        <span className="text-neutral-300">|</span>
                        <button
                          onClick={() => setSelectedSuggestions(new Set())}
                          className="text-xs text-neutral-600 hover:text-neutral-700 font-medium"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    {suggestions.map((suggestion, idx) => {
                      const isSelected = selectedSuggestions.has(suggestion.requirementEntity);
                      return (
                        <Card
                          key={idx}
                          className={`cursor-pointer transition-all ${
                            isSelected
                              ? 'border-primary-400 bg-primary-50 shadow-sm'
                              : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                          onClick={() => toggleSelection(suggestion.requirementEntity)}
                        >
                          <CardBody>
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <div className="flex-shrink-0 mt-0.5">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected
                                    ? 'bg-primary-600 border-primary-600'
                                    : 'bg-white border-neutral-300'
                                }`}>
                                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                              </div>

                              {/* Icon */}
                              <div className="flex-shrink-0 mt-0.5">
                                {suggestion.requirementCategory === 'master_data' ? (
                                  <Database className="w-5 h-5 text-primary-600" />
                                ) : (
                                  <FileText className="w-5 h-5 text-secondary-600" />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-sm font-semibold text-neutral-900">
                                    {suggestion.requirementEntity}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    suggestion.confidence === 'high'
                                      ? 'bg-green-100 text-green-700'
                                      : suggestion.confidence === 'medium'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {suggestion.confidence.toUpperCase()} confidence
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                    suggestion.requirementCategory === 'master_data'
                                      ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                      : 'bg-orange-100 text-orange-700 border border-orange-200'
                                  }`}>
                                    {suggestion.requirementCategory === 'master_data' ? 'Master Data' : 'Transactional'}
                                  </span>
                                  {suggestion.matchesExisting && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 border border-green-300">
                                      ✓ Exists in Neo4j
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-600 mb-1">
                                  {suggestion.reasoning}
                                </p>
                                {suggestion.typicalLocation && (
                                  <p className="text-xs text-neutral-500 italic">
                                    📍 {suggestion.typicalLocation}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between bg-neutral-50">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isResearching || isApproving}
            >
              Cancel
            </Button>
            {hasResearched && suggestions.length > 0 && (
              <Button
                onClick={handleApprove}
                disabled={selectedSuggestions.size === 0 || isApproving}
                loading={isApproving}
              >
                <Check className="w-4 h-4" />
                Add {selectedSuggestions.size} {selectedSuggestions.size === 1 ? 'Source' : 'Sources'} to {systemName}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
